<template>
    <Head :title="report?.name || t('teamPerspective.title')" />
    <BaseLayout gap="sm" class="fs-mask" fill-height>
        <header class="flex flex-wrap items-start gap-4">
            <BaseLink :href="back_url" :aria-label="t('teamPage.allTeams')" class="mt-2 shrink-0 text-ink-muted hover:text-ink"
                ><i class="bi bi-chevron-left" aria-hidden="true"
            /></BaseLink>
            <div class="flex min-w-0 flex-1 flex-col gap-2">
                <h1 class="display-serif break-words text-ink">{{ report?.name || t("teamPerspective.heading") }}</h1>
                <p v-if="report" class="body-small text-ink-muted">
                    {{ report.can_manage ? t("teamPage.managedCount", { count: report.members.length }) : t("teamPage.memberCount", { count: report.members.length }) }}
                </p>
            </div>
            <ManageTeamDialog
                v-if="report"
                :key="report.id"
                :report="report"
                :leave-url="leave_url"
                :delete-url="delete_url"
                @deleted="router.visit(back_url)"
                @updated="membershipUpdated"
                @left="router.visit(back_url)" />
        </header>
        <BaseCard v-if="!report" padding="lg"
            ><p class="body text-ink">{{ t("teamPerspective.noTeams") }}</p></BaseCard
        >
        <template v-else>
            <BaseTabs
                v-model="activeSection"
                :tab-ids="['overview', 'breakdown', 'members']"
                :labels="{ overview: t('teamPage.overview'), breakdown: t('teamPage.breakdown'), members: t('teamPage.members') }"
                content-class="pt-6">
                <template #content-overview><TeamOverview :report="report" /></template>
                <template #content-members><TeamMembers :report="report" /></template>
                <template #content-breakdown>
                    <div class="flex flex-col gap-6">
                        <p class="body-small text-ink-muted" role="status">{{ t("teamPerspective.reportCount", { count: resultCount, total: includedMembers.length }) }}</p>
                        <BaseCard v-if="!dimensions.length" padding="lg"
                            ><p class="body text-ink-muted">{{ t("teamPerspective.empty") }}</p></BaseCard
                        >
                        <TeamBreakdown v-else :key="report.id" :dimensions="dimensions" :members="report.members" :viewer-id="report.viewer_id" />
                        <p class="details text-ink-muted">{{ t("teamPerspective.sharing") }}</p>
                    </div>
                </template>
            </BaseTabs>
            <footer class="sticky z-20 mt-auto pt-6 md:bottom-4" :class="keyboardHeight ? 'bottom-4' : 'bottom-[calc(var(--mobile-nav-total-height,0px)+1rem)]'">
                <HomeChatInput
                    class="drop-shadow-md"
                    compact
                    :auto-focus-input="false"
                    :start-chat-url="start_chat_url"
                    chat-source="team_perspective"
                    :default-chat-redirect-url="default_chat_redirect_url"
                    :document-upload-enabled="document_upload_enabled"
                    :visual-document-analysis-enabled="visual_document_analysis_enabled"
                    :work-document-capabilities-url="work_document_capabilities_url"
                    :pii-patterns="pii_patterns?.map((pattern) => ({ ...pattern, keywords: pattern.keywords ?? null }))"
                    :better-voice-composer-enabled="better_voice_composer_enabled"
                    :placeholder="t('teamPage.askNadia')" />
            </footer>
        </template>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>
<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useUrlSearchParams } from "@vueuse/core";
import type { TeamPerspectivePage, TeamPerspectiveReport } from "~vue/api/generated/team-perspective/types.gen";
import HomeChatInput from "~vue/ChatHome/HomeChatInput.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import { useKeyboard } from "~vue/composables/useKeyboard";
import ManageTeamDialog from "~vue/Perspective/ManageTeamDialog.vue";
import TeamBreakdown from "~vue/Perspective/TeamBreakdown.vue";
import TeamMembers from "~vue/Perspective/TeamMembers.vue";
import TeamOverview from "~vue/Perspective/TeamOverview.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
const props = defineProps<TeamPerspectivePage>();
const { t } = useI18n();
const { keyboardHeight } = useKeyboard();
const report = ref(props.report);
const parameters = useUrlSearchParams("history", { writeMode: "push" });
const activeSection = computed({
    get: () => (["overview", "breakdown", "members"].includes(String(parameters.tab)) ? String(parameters.tab) : "overview"),
    set: (tab: string) => {
        parameters.tab = tab === "overview" ? "" : tab;
    },
});
const includedMembers = computed(() => report.value?.members.filter((member) => report.value?.selected_member_ids.includes(member.id)) ?? []);
const resultCount = computed(() => includedMembers.value.filter((member) => member.result?.dimensions.length).length);
const dimensions = computed(() => [...(report.value?.dimensions ?? [])].sort((a, b) => ["EI", "SN", "TF", "JP"].indexOf(a.id) - ["EI", "SN", "TF", "JP"].indexOf(b.id)));
watch(
    () => props.report,
    (value) => {
        report.value = value;
    },
);
function membershipUpdated(value: TeamPerspectiveReport) {
    report.value = value;
}
</script>
